/**
 * Clipboard helpers for rich-text surfaces (the Tiptap editor's
 * `handlePaste` and the omnibar's document-level paste fallback).
 *
 * Lives in its own file (separate from `rich-text-editor.tsx`) so
 * consumers outside the lazy-loaded Tiptap chunk can call
 * `extractFiles` without pulling Tiptap into the main bundle.
 *
 * Specifically: `draft-bar.tsx` mounts on every screen via the omnibar
 * host, but `<RichTextEditor>` itself only mounts when the user
 * activates Add mode. By keeping the document-level paste handler's
 * helper in this side file, the bar's bare existence doesn't drag
 * ~500 KB of Tiptap onto the cold-load critical path — Tiptap only
 * loads when `<RichTextEditor>` is actually rendered.
 */
/**
 * Defence-in-depth cap on a single paste event. The block-store and
 * server both enforce their own MAX_FILES, but capping at the paste
 * boundary keeps a pathological clipboard (some apps carry many
 * embedded files) from spawning dozens of optimistic placeholders
 * before the lower layers reject the overflow.
 */
export const MAX_PASTE_FILES = 10;
/**
 * Pull any files out of a `DataTransfer` (the shape of both
 * `ClipboardEvent.clipboardData` and `DragEvent.dataTransfer`).
 *
 * Used by:
 *   - `rich-text-editor.tsx`'s `editorProps.handlePaste` — intercepts
 *     file pastes while focus is inside the Tiptap contenteditable
 *     so the editor doesn't try to embed a binary blob it can't render.
 *   - `draft-bar.tsx`'s document-level paste handler — the catch-all
 *     "paste anywhere outside an input" path; stages the file(s) as
 *     attachments via the omnibar's draft pipeline.
 *
 * Filters on `kind === "file"` only — the `kind` filter excludes the
 * `text/plain` / `text/html` mirror entries every clipboard carries
 * alongside files. A clipboard that only carries plain text returns
 * `[]`, leaving the caller free to fall through to its text path; a
 * clipboard carrying both files AND text (Notion screenshot copy is
 * one example, where a `text/plain` mirror sits next to the image
 * file) treats the file as the primary intent — matches the user's
 * mental model of "I copied a screenshot / PDF / archive, paste
 * should attach it".
 *
 * MIME type isn't filtered: pasting a `.pdf`, `.zip`, voice memo, or
 * any other binary should land as a staged attachment the same way
 * an image does. The omnibar's `stageFiles` path branches on
 * `type.startsWith("image/")` for the preview chip (thumbnail vs
 * filename), but the extraction itself is type-agnostic.
 */
export function extractFiles(clipboard) {
    if (clipboard === null)
        return [];
    const items = Array.from(clipboard.items);
    return items
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile())
        .filter((f) => f !== null)
        .slice(0, MAX_PASTE_FILES);
}
